/**
 * The enrollment fee: what it comes to for each child and in all, and Pay now
 * once the invoice can be paid (unpaid, or its payment failed). While a bank
 * payment is on its way it says so; while the fee is being prepared (a draft,
 * or one called off and being made again) it offers nothing, and a draft says
 * it's being prepared. Paid, it says when.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '~/components/ui/button';
import { formatChecklistDate } from '~/pages/FamilyDetail/checklistWords';
import { PayInvoiceDialog, type PayableInvoice } from '~/pages/MyInvoices/PayInvoiceDialog';
import { formatCurrency } from '~/utils/paymentUtils';
import { Section, StepHeader, type StepProps } from './stepParts';
import { useFamilyEnrollmentForYear } from './useFamilyEnrollmentForYear';

// The invoice statuses a family can pay, as the server's PayInvoiceWithSavedMethod allows.
const PAYABLE_STATUSES = ['UNPAID', 'PAYMENT_FAILED'];

export default function FeeStep({ step, readOnly, overviewPath }: StepProps) {
  const fee = step.fee;
  const navigate = useNavigate();
  const { familyEnrollmentId } = useFamilyEnrollmentForYear();
  const [paying, setPaying] = useState(false);
  if (!fee) return null;

  const paid = !!fee.paidAt;
  const processing = !paid && fee.status === 'PAYMENT_PROCESSING';
  const invoice: PayableInvoice | null =
    fee.invoiceId && fee.totalCents != null ? { invoiceId: fee.invoiceId, amountCents: fee.totalCents, memo: step.item.title } : null;
  const canPay = !paid && !readOnly && !!invoice && !!familyEnrollmentId && PAYABLE_STATUSES.includes(fee.status ?? '');

  return (
    <div className="space-y-6">
      <StepHeader step={step} notNeeded="Not needed" />
      {/* Not needed (None, or no one coming) has nothing to show: no amount per child, no card. */}
      {step.item.state === 'NOT_NEEDED' ? null : (
        <Section>
          {fee.amountCentsPerChild > 0 ? (
            <p className="text-muted-foreground">{formatCurrency(fee.amountCentsPerChild)} per child</p>
          ) : null}
          {fee.children.length > 0 ? (
            <ul className="space-y-2">
              {fee.children.map((child, index) => (
                <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="min-w-0 break-words font-medium">{child.name}</span>
                  <span className="tabular-nums">{formatCurrency(child.amountCents)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {fee.totalCents !== null && fee.totalCents !== undefined ? (
            <p className="flex items-baseline justify-between gap-3 border-t pt-3 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{formatCurrency(fee.totalCents)}</span>
            </p>
          ) : null}
          {paid ? <p className="text-sm font-medium text-green-700">Paid {formatChecklistDate(fee.paidAt)}</p> : null}
          {processing ? <p className="text-sm font-medium text-muted-foreground">Payment processing</p> : null}
          {!paid && fee.status === 'DRAFT' ? <p className="text-sm text-muted-foreground">Your fee is being prepared.</p> : null}
          {canPay ? (
            <Button type="button" size="lg" className="min-h-11 w-full md:w-auto" onClick={() => setPaying(true)}>
              Pay now
            </Button>
          ) : null}
        </Section>
      )}
      {canPay ? (
        <PayInvoiceDialog
          invoice={invoice}
          familyEnrollmentId={familyEnrollmentId}
          open={paying}
          onOpenChange={setPaying}
          onPaid={() => navigate(overviewPath)}
        />
      ) : null}
    </div>
  );
}
